import { Assignment, EventBusy, HourglassTop } from "@mui/icons-material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Alert, Box, CircularProgress, Divider, Grid2, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { getUserCounters, getUserInvitedProcedures } from "../../api/app";
import { useTranslationHook } from "../../hooks";
import { TenderProcedure } from "../../types/model/procedure/TenderProcedure";
import { UserCounters } from "../../types/model/stats";
import { ProcedureList } from "../Procedure";
import { SectionAccordion } from "./SectionAccordion";
import StatCard from "./StatCard";

type ProcedureFilter = "total" | "active" | "ending" | null;
type ProcedureOrderBy = "ending_soon" | "latest_invited";

const SupplierDashboard = () => {
  const { t } = useTranslationHook();
  const navigate = useNavigate();

  const [counters, setCounters] = useState<UserCounters>();
  const [countersLoading, setCountersLoading] = useState(false);

  const [procedures, setProcedures] = useState<TenderProcedure[]>([]);
  const [proceduresLoading, setProceduresLoading] = useState(false);

  const [activeFilter, setActiveFilter] = useState<ProcedureFilter>("total");
  const [proceduresExpanded, setProceduresExpanded] = useState(true);

  useEffect(() => {
    fetchCounters();
    handleFilterChange(activeFilter);
  }, []);

  const fetchCounters = async () => {
    setCountersLoading(true);
    try {
      setCounters(await getUserCounters());
    } catch (error) {
      console.error(error);
    } finally {
      setCountersLoading(false);
    }
  };

  const fetchUserInvitedProcedures = async (isOpen?: boolean, orderBy?: ProcedureOrderBy) => {
    setProceduresLoading(true);
    try {
      setProcedures(await getUserInvitedProcedures({ isOpen, orderBy }));
    } catch (error) {
      console.error(error);
    } finally {
      setProceduresLoading(false);
    }
  };

  const handleFilterChange = (filter: ProcedureFilter) => {
    setActiveFilter(filter);

    switch (filter) {
      case "total":
        fetchUserInvitedProcedures();
        break;
      case "active":
        fetchUserInvitedProcedures(true, "latest_invited");
        break;
      case "ending":
        fetchUserInvitedProcedures(true, "ending_soon");
        break;
      default:
        fetchUserInvitedProcedures();
        break;
    }
  };

  const handleRequestRefreshProcedures = () => {
    handleFilterChange(activeFilter);
  };

  const handleUserRowClickProcedure = (procedure: TenderProcedure) => {
    navigate(
      `/accounts/${procedure.accountId}/projects/${procedure.projectId}/activities/${procedure.activityId}/procedures/${procedure.id}`,
    );
  };

  let countersContent;
  if (countersLoading) {
    countersContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (counters) {
    countersContent = (
      <Grid2 container spacing={2} columns={12}>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<Assignment />}
            title={t("generic.stats.total-procedures")}
            total={counters.invitedProcedures.total}
            isActive={activeFilter === "total"}
            onClick={() => handleFilterChange("total")}
            accentColor="#2563eb"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<HourglassTop />}
            title={t("generic.stats.active-procedures")}
            active={counters.invitedProcedures.active}
            isActive={activeFilter === "active"}
            onClick={() => handleFilterChange("active")}
            accentColor="#16a34a"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            icon={<EventBusy />}
            title={t("generic.stats.ending-procedures")}
            ending={counters.invitedProcedures.ending}
            isActive={activeFilter === "ending"}
            onClick={() => handleFilterChange("ending")}
            accentColor="#d97706"
          />
        </Grid2>
      </Grid2>
    );
  } else {
    countersContent = (
      <Typography align="center" sx={{ mt: 2 }}>
        {t("generic.stats.no-data")}
      </Typography>
    );
  }

  let proceduresContent;
  if (proceduresLoading) {
    proceduresContent = (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  } else if (procedures.length > 0) {
    proceduresContent = (
      <SectionAccordion
        title={t("generic.stats.invited-procedures")}
        count={procedures.length}
        expanded={proceduresExpanded}
        onToggle={() => setProceduresExpanded((e) => !e)}
      >
        <ProcedureList
          procedures={procedures}
          loading={proceduresLoading}
          onRefresh={handleRequestRefreshProcedures}
          onProcedureSelected={(p) => handleUserRowClickProcedure(p as TenderProcedure)}
          readOnly={true}
          activeFilter={activeFilter}
          onActiveFilterChange={handleFilterChange}
        />
      </SectionAccordion>
    );
  } else {
    proceduresContent = (
      <Alert
        severity="info"
        icon={<WarningAmberRoundedIcon />}
        sx={{ borderRadius: 2 }}
      >
        {t("generic.stats.no-invited-procedures")}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: 2 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          {t("generic.stats.overview")}
        </Typography>

        {countersContent}

        <Stack direction="row" alignItems="center" spacing={2} sx={{ my: 4 }}>
          <Divider sx={{ flexGrow: 1 }} />
          <Typography
            variant="overline"
            sx={{ color: "text.disabled", fontWeight: 600, letterSpacing: 1 }}
          >
            {t("generic.stats.operational-summary")}
          </Typography>
          <Divider sx={{ flexGrow: 1 }} />
        </Stack>

        {proceduresContent}
      </Paper>
    </Box>
  );
};

export default SupplierDashboard;
